/* ═══════════════════════════════════════════════════════════════
   SmartBlockService — firm-scoped Smart Block + Draft CRUD via EC2 API.

   A SmartBlock is a dynamic, context-aware spec content element. Lifecycle
   mirrors SpecMaster: any firm member can draft, publishers approve.

   Two tables:
     smart_blocks         — published source of truth
     smart_block_drafts   — working copies
   ═══════════════════════════════════════════════════════════════ */

import type { SmartBlock, SmartBlockDraft } from '../../shared/types';
import * as cache from './cloudCache';
import type { TableOpts } from './cloudCache';
import { log as logActivity } from './activityLogger';

const smartBlockOpts: TableOpts = {
  jsonFields: [
    'tags',
    'paramSchema',
    'defaultParams',
    'querySpec',
    'versionHistory',
    'editHistory',
  ],
};

const draftOpts: TableOpts = {
  jsonFields: ['tags', 'paramSchema', 'defaultParams', 'querySpec', 'publishRequest'],
};

export const SmartBlockService = {
  /* ── Published ── */
  list: async (): Promise<SmartBlock[]> => cache.list<SmartBlock>('smart_blocks', smartBlockOpts),

  get: async (id: string): Promise<SmartBlock | null> => {
    const all = await SmartBlockService.list();
    return all.find((m) => m.id === id) || null;
  },

  save: async (sb: SmartBlock): Promise<SmartBlock> => {
    const isNew = !sb.id;
    const saved = await cache.save('smart_blocks', sb, smartBlockOpts);
    logActivity({
      action: isNew ? 'smart_block.publish' : 'smart_block.update',
      entityType: 'smart_block',
      entityId: saved.id,
      metadata: { name: (saved as any).name, kind: (saved as any).kind },
    });
    return saved;
  },

  delete: async (id: string): Promise<void> => {
    await cache.remove('smart_blocks', id);
    logActivity({
      action: 'smart_block.delete',
      entityType: 'smart_block',
      entityId: id,
    });
  },

  archive: async (sb: SmartBlock, archivedBy: string): Promise<SmartBlock> => {
    const updated = {
      ...sb,
      status: 'archived' as const,
      archivedAt: new Date().toISOString(),
      archivedBy,
    };
    const saved = await cache.save('smart_blocks', updated, smartBlockOpts);
    logActivity({
      action: 'smart_block.archive',
      entityType: 'smart_block',
      entityId: saved.id,
    });
    return saved;
  },

  restore: async (sb: SmartBlock): Promise<SmartBlock> => {
    const updated = {
      ...sb,
      status: 'published' as const,
      archivedAt: null,
      archivedBy: null,
    };
    const saved = await cache.save('smart_blocks', updated, smartBlockOpts);
    logActivity({
      action: 'smart_block.restore',
      entityType: 'smart_block',
      entityId: saved.id,
    });
    return saved;
  },

  /* ── Drafts ── */
  listDrafts: async (): Promise<SmartBlockDraft[]> =>
    cache.list<SmartBlockDraft>('smart_block_drafts', draftOpts),

  getDraft: async (id: string): Promise<SmartBlockDraft | null> => {
    const all = await SmartBlockService.listDrafts();
    return all.find((d) => d.id === id) || null;
  },

  saveDraft: async (draft: SmartBlockDraft): Promise<SmartBlockDraft> => {
    const saved = await cache.save('smart_block_drafts', draft, draftOpts);
    logActivity({
      action: 'smart_block.draft_save',
      entityType: 'smart_block_draft',
      entityId: saved.id,
    });
    return saved;
  },

  deleteDraft: async (id: string): Promise<void> => cache.remove('smart_block_drafts', id),

  /* ── Submit for review ── */
  submitForReview: async (
    draft: SmartBlockDraft,
    requestedBy: string,
    message?: string
  ): Promise<SmartBlockDraft> => {
    const next: SmartBlockDraft = {
      ...draft,
      publishRequest: {
        id: `pr-${Date.now()}`,
        draftId: draft.id,
        blueprintId: draft.smartBlockId,
        requestedBy,
        requestedAt: new Date().toISOString(),
        message,
        status: 'pending',
      },
      lastSaved: new Date().toISOString(),
    };
    const saved = await cache.save('smart_block_drafts', next, draftOpts);
    logActivity({
      action: 'smart_block.submit_for_review',
      entityType: 'smart_block_draft',
      entityId: saved.id,
      metadata: { smartBlockId: draft.smartBlockId },
    });
    return saved;
  },

  /* ── Approve a draft → bumps version on the published row,
        clears the draft, snapshots prior version into version_history. ── */
  approveDraft: async (
    draft: SmartBlockDraft,
    approverId: string,
    approverName?: string
  ): Promise<SmartBlock> => {
    const existing = draft.smartBlockId ? await SmartBlockService.get(draft.smartBlockId) : null;
    const now = new Date().toISOString();
    const nextVersion = (existing?.version ?? 0) + 1;
    const versionHistory = (existing?.versionHistory ?? []).slice();
    if (existing) {
      versionHistory.push({
        version: existing.version,
        snapshot: {
          name: existing.name,
          kind: existing.kind,
          paramSchema: existing.paramSchema,
          defaultParams: existing.defaultParams,
          querySpec: existing.querySpec,
          templateSpec: existing.templateSpec,
          icon: existing.icon,
          color: existing.color,
          category: existing.category,
        },
        savedBy: existing.modifiedBy ?? existing.creator ?? '',
        savedAt: existing.lastModified,
      });
    }
    const maxKept = existing?.maxVersionsKept ?? 10;
    while (versionHistory.length > maxKept) versionHistory.shift();
    const next: SmartBlock = {
      id: draft.smartBlockId || '',
      firmId: existing?.firmId,
      name: draft.name ?? existing?.name ?? 'Untitled',
      description: draft.description ?? existing?.description,
      kind: (draft.kind ?? existing?.kind ?? 'custom') as SmartBlock['kind'],
      icon: draft.icon ?? existing?.icon,
      color: draft.color ?? existing?.color,
      category: draft.category ?? existing?.category,
      tags: draft.tags ?? existing?.tags,
      paramSchema: draft.paramSchema ?? existing?.paramSchema,
      defaultParams: draft.defaultParams ?? existing?.defaultParams,
      querySpec: draft.querySpec ?? existing?.querySpec,
      templateSpec: draft.templateSpec ?? existing?.templateSpec,
      status: 'published',
      version: nextVersion,
      versionHistory,
      maxVersionsKept: maxKept,
      creator: existing?.creator ?? approverName ?? approverId,
      owner: existing?.owner ?? approverName ?? approverId,
      modifiedBy: approverName ?? approverId,
      lastModified: now,
      createdAt: existing?.createdAt ?? now,
    };
    const saved = await cache.save('smart_blocks', next, smartBlockOpts);
    await cache.remove('smart_block_drafts', draft.id);
    logActivity({
      action: 'smart_block.approve_review',
      entityType: 'smart_block',
      entityId: saved.id,
      metadata: { version: saved.version },
    });
    return saved;
  },

  rejectDraft: async (
    draft: SmartBlockDraft,
    reviewerId: string,
    note?: string
  ): Promise<SmartBlockDraft> => {
    if (!draft.publishRequest) return draft;
    const next: SmartBlockDraft = {
      ...draft,
      publishRequest: {
        ...draft.publishRequest,
        status: 'rejected',
        reviewedBy: reviewerId,
        reviewedAt: new Date().toISOString(),
        reviewNote: note,
      },
      lastSaved: new Date().toISOString(),
    };
    const saved = await cache.save('smart_block_drafts', next, draftOpts);
    logActivity({
      action: 'smart_block.reject_review',
      entityType: 'smart_block_draft',
      entityId: saved.id,
      metadata: { smartBlockId: draft.smartBlockId },
    });
    return saved;
  },

  /* ── Checkout: create draft from published row ── */
  checkout: async (smartBlockId: string, editorId: string): Promise<SmartBlockDraft> => {
    const existing = await SmartBlockService.get(smartBlockId);
    if (!existing) {
      throw new Error('Cannot check out a smart block that does not exist.');
    }
    // If this user already has an open draft against this smart block, return it.
    const allDrafts = await SmartBlockService.listDrafts();
    const mine = allDrafts.find((d) => d.smartBlockId === smartBlockId && d.editedBy === editorId);
    if (mine) return mine;
    const now = new Date().toISOString();
    const draft: SmartBlockDraft = {
      id: '',
      smartBlockId,
      baseVersion: existing.version,
      editedBy: editorId,
      name: existing.name,
      description: existing.description,
      kind: existing.kind,
      icon: existing.icon,
      color: existing.color,
      category: existing.category,
      tags: existing.tags,
      paramSchema: existing.paramSchema,
      defaultParams: existing.defaultParams,
      querySpec: existing.querySpec,
      templateSpec: existing.templateSpec,
      createdAt: now,
      lastSaved: now,
    };
    return SmartBlockService.saveDraft(draft);
  },
};
